// @ts-check
import jwt_decode from 'jwt-decode';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * @typedef {Object} AuthState
 * @property {string|null} token - Token JWT de autenticação
 * @property {boolean} logged - Indica se o usuário está autenticado
 * @property {() => Object|null} getTokenPayload - Retorna os dados decodificados do token JWT
 * @property {(token: string|null) => void} setToken - Define o token de autenticação
 * @property {() => void} logout - Remove o token e desloga o usuário
 */

/**
 * Store Zustand para gerenciamento de autenticação
 *
 * Este store utiliza o middleware `persist` do Zustand para manter o estado de autenticação
 * persistido no `localStorage` do navegador. Isso permite que o usuário permaneça autenticado
 * mesmo após recarregar a página ou fechar e reabrir o navegador.
 *
 * **Armazenamento:**
 * - Chave no localStorage: `welm-auth-storage`
 * - Dados persistidos: `token` e `logged`
 *
 * **Funcionalidades:**
 * - Armazenamento seguro do token JWT
 * - Decodificação automática do token para obter payload
 * - Gerenciamento do estado de login/logout
 * - Persistência automática no localStorage
 *
 * @type {import('zustand').UseBoundStore<import('zustand').StoreApi<AuthState>>}
 *
 * @example
 * // Obter estado de autenticação
 * import { useAuthStore } from '@welm/stores/authStore';
 *
 * function MeuComponente() {
 *   const { logged, token } = useAuthStore();
 *
 *   return <div>{logged ? 'Autenticado' : 'Não autenticado'}</div>;
 * }
 *
 * @example
 * // Definir token após login
 * import { useAuthStore } from '@welm/stores/authStore';
 *
 * function LoginComponent() {
 *   const setToken = useAuthStore(state => state.setToken);
 *
 *   const handleLogin = async () => {
 *     const response = await fetch('/api/login', {
 *       method: 'POST',
 *       body: JSON.stringify({ email, senha })
 *     });
 *     const { token } = await response.json();
 *     setToken(token);
 *   };
 * }
 *
 * @example
 * // Obter dados do usuário do token
 * import { useAuthStore } from '@welm/stores/authStore';
 *
 * function UserProfile() {
 *   const getTokenPayload = useAuthStore(state => state.getTokenPayload);
 *   const userData = getTokenPayload();
 *
 *   return <div>Olá, {userData?.nome}</div>;
 * }
 *
 * @example
 * // Fazer logout
 * import { useAuthStore } from '@welm/stores/authStore';
 *
 * function LogoutButton() {
 *   const logout = useAuthStore(state => state.logout);
 *
 *   return <button onClick={logout}>Sair</button>;
 * }
 *
 * @example
 * // Uso direto sem hook (fora de componentes React)
 * import { useAuthStore } from '@welm/stores/authStore';
 *
 * const token = useAuthStore.getState().token;
 * const isLogged = useAuthStore.getState().logged;
 * useAuthStore.getState().setToken('novo-token-jwt');
 *
 * @remarks
 * - O token é armazenado de forma persistente no localStorage
 * - O estado `logged` é automaticamente atualizado ao definir/remover o token
 * - A função `getTokenPayload()` decodifica o token JWT sem validar a assinatura
 * - Ao chamar `setToken(null)`, o usuário é deslogado automaticamente
 * - O logout limpa tanto o token quanto o estado logged
 * - A persistência é automática, não requer ação manual
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      /**
       * Token JWT de autenticação do usuário
       * @type {string|null}
       */
      token: null,

      /**
       * Indica se o usuário está autenticado
       * @type {boolean}
       */
      logged: false,

      /**
       * Retorna os dados decodificados do token JWT
       * @returns {Object|null} Payload do token decodificado ou null se não houver token
       */
      getTokenPayload: () => {
        const token = get().token;
        if (token) {
          const decoded = jwt_decode(token);
          return decoded;
        } else {
          return null;
        }
      },

      /**
       * Define ou remove o token de autenticação
       * @param {string|null} token - Token JWT a ser armazenado ou null para remover
       */
      setToken: (token) => {
        if (token) {
          set({ token, logged: true });
        } else {
          set({ logged: false, token: null });
        }
      },

      /**
       * Remove o token e desloga o usuário
       */
      logout: () => set({ token: null, logged: false }),
    }),
    {
      name: 'welm-auth-storage',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          localStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
